import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, DownloadSimple, Printer, ArrowsClockwise, Prohibit, MapPin, Storefront, ClockCounterClockwise } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import OneKiteQR from "@/components/OneKiteQR";

const getSvg = (id) => document.querySelector(`#${id} svg`);

const downloadQR = (id, name) => {
  const svg = getSvg(id);
  if (!svg) return;
  const xml = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  img.onload = () => {
    const s = 640;
    const canvas = document.createElement("canvas");
    canvas.width = s; canvas.height = s;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, s, s);
    ctx.drawImage(img, 40, 40, s - 80, s - 80);
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${name}.png`;
    a.click();
  };
  img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
};

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const printQR = (id, title, subtitle) => {
  const svg = getSvg(id);
  if (!svg) return;
  const xml = new XMLSerializer().serializeToString(svg);
  const w = window.open("", "_blank", "width=480,height=640");
  if (!w) return toast.error("Allow pop-ups to print");
  w.document.write(`<html><head><title>${esc(title)}</title></head>
    <body style="font-family:system-ui,sans-serif;text-align:center;padding:48px">
    <div style="display:inline-block;padding:24px;border-radius:24px;background:#fff">${xml}</div>
    <h2 style="margin:24px 0 4px">${esc(title)}</h2>
    <p style="color:#6b7280;margin:0">${esc(subtitle || "")}</p>
    <p style="color:#9ca3af;font-size:12px;margin-top:16px">Scan with the OneKite app</p>
    </body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
};

const StatusBadge = ({ status }) => (
  <span data-testid="qr-status" className={`text-[10px] font-medium rounded-full px-2 py-0.5 ${status === "active" ? "text-primary bg-primary/10 border border-primary/30" : "text-destructive bg-destructive/10 border border-destructive/30"}`}>
    {status === "active" ? "Active" : "Revoked"}
  </span>
);

function QRCard({ qr, title, subtitle, icon: Icon, onRegenerate, onRevoke, testid, scans = [] }) {
  const domId = `qr-${qr.id}`;
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-start gap-4">
        <div id={domId} className="shrink-0">
          <OneKiteQR value={qr.status === "active" ? qr.token : null} size={130} />
        </div>
        <div className="min-w-0 w-60 shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Icon size={18} className="text-primary" />
            <p className="font-display font-semibold truncate">{title}</p>
            <StatusBadge status={qr.status} />
          </div>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
            <p>Version {qr.version} · <span data-testid="qr-scan-count">{qr.scan_count}</span> scans</p>
            <p>{qr.last_scanned_at ? `Last scan ${fmtDateTime(qr.last_scanned_at)}` : "Not scanned yet"}</p>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button data-testid={`${testid}-download`} size="sm" variant="outline" disabled={qr.status !== "active"} onClick={() => downloadQR(domId, title.replace(/\s+/g, "_"))} className="rounded-lg h-8 border-black/10">
              <DownloadSimple size={15} className="mr-1" /> Download
            </Button>
            <Button data-testid={`${testid}-print`} size="sm" variant="outline" disabled={qr.status !== "active"} onClick={() => printQR(domId, title, subtitle)} className="rounded-lg h-8 border-black/10">
              <Printer size={15} className="mr-1" /> Print
            </Button>
            <Button data-testid={`${testid}-regenerate`} size="sm" variant="outline" onClick={() => onRegenerate(qr)} className="rounded-lg h-8 border-black/10">
              <ArrowsClockwise size={15} className="mr-1" /> Regenerate
            </Button>
            {qr.status === "active" && (
              <Button data-testid={`${testid}-revoke`} size="sm" variant="outline" onClick={() => onRevoke(qr)} className="rounded-lg h-8 border-destructive/30 text-destructive hover:bg-destructive/10">
                <Prohibit size={15} className="mr-1" /> Revoke
              </Button>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0 border-l border-black/8 pl-4 hidden sm:block" data-testid={`${testid}-scans`}>
          <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><ClockCounterClockwise size={12} /> Scans ({qr.scan_count})</p>
          {scans.length === 0 ? <p className="text-xs text-muted-foreground">No scans yet.</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 max-h-40 overflow-y-auto pr-1">
              {scans.map((s, i) => (
                <div key={i} className="text-xs flex items-center justify-between gap-2 border-b border-black/5 pb-1">
                  <span className="text-foreground capitalize">Scanned by {s.scanner_role}</span>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">{fmtDateTime(s.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BizQRCodes() {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    api.get("/business/qr").then((r) => setData(r.data)).catch((e) => { setError(true); toast.error(apiError(e.response?.data?.detail)); });
    api.get("/business/qr/activity").then((r) => setActivity(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const regenerate = async (qr) => {
    if (!window.confirm("Regenerate this QR? The current printed/shared code will stop working. Your OneKite ID stays the same.")) return;
    try { await api.post("/business/qr/regenerate", { qr_id: qr.id }); toast.success("QR regenerated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const revoke = async (qr) => {
    if (!window.confirm("Revoke this QR? It will stop resolving until you regenerate it.")) return;
    try { await api.post("/business/qr/revoke", { qr_id: qr.id }); toast.success("QR revoked"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (error && !data) return (
    <div className="pt-16 text-center space-y-3" data-testid="qr-page-error">
      <p className="text-muted-foreground">Couldn't load your QR codes.</p>
      <Button onClick={load} variant="outline" className="rounded-xl border-black/10">Retry</Button>
    </div>
  );
  if (!data) return <div className="pt-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="qr-page">
      <div>
        <h1 className="font-display font-bold text-3xl flex items-center gap-2"><QrCode size={26} className="text-primary" /> QR Codes</h1>
        <p className="text-muted-foreground text-sm">Secure, opaque codes customers scan to open {data.business.name}. Regenerating a code never changes your OneKite ID <span className="font-mono text-foreground">{data.business.onekite_id_formatted}</span>.</p>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">Business QR</p>
        <QRCard qr={data.business_qr} title={data.business.name} subtitle={data.business.onekite_name ? `@${data.business.onekite_name}` : ""} icon={Storefront} onRegenerate={regenerate} onRevoke={revoke} testid="business-qr-card" scans={activity.filter((s) => s.qr_type === "BUSINESS_QR")} />
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">Branch QRs</p>
        <div className="space-y-3">
          {data.branch_qrs.map((b) => (
            <QRCard key={b.id} qr={b} title={b.branch_name} subtitle={[b.is_main ? "Main branch" : null, b.branch_address].filter(Boolean).join(" · ")} icon={MapPin} onRegenerate={regenerate} onRevoke={revoke} testid={`branch-qr-${b.branch_id}`} scans={activity.filter((s) => s.qr_type === "BRANCH_QR" && s.branch_name === b.branch_name)} />
          ))}
        </div>
      </div>
    </div>
  );
}
